import { ZButton } from "@/shared/components/button";
import { Input, Modal, ModalBody, ModalContent } from "@nextui-org/react";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { Questionnaire } from "../../requests/questionnaire";

interface QuestionnaireRenameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRename: (newName: string) => void;
  questionnaire: Questionnaire | null;
  isRenaming?: boolean;
  errorMessage?: string;
}

const QuestionnaireRenameModal = ({
  isOpen,
  onClose,
  onRename,
  questionnaire,
  isRenaming = false,
  errorMessage,
}: QuestionnaireRenameModalProps) => {
  const [questionnaireName, setQuestionnaireName] = useState("");

  useEffect(() => {
    if (isOpen && questionnaire) {
      setQuestionnaireName(questionnaire.questionnaire_name);
    }
  }, [isOpen, questionnaire]);

  const handleClose = () => {
    setQuestionnaireName("");
    onClose();
  };

  const handleSubmit = () => {
    if (questionnaireName.trim() && questionnaireName.trim() !== questionnaire?.questionnaire_name) {
      onRename(questionnaireName.trim());
    }
  };

  const isSubmitDisabled = !questionnaireName.trim() ||
                          questionnaireName.trim() === questionnaire?.questionnaire_name ||
                          isRenaming;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="md"
      hideCloseButton={true}
      classNames={{
        base: "max-w-md",
        wrapper: "z-[9999] p-2",
      }}
    >
      <ModalContent className="max-h-[500px]">
        <ModalBody className="p-6">
          <div className="flex flex-col gap-6 w-full">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-zinc-900 text-center flex-grow">
                Rename Questionnaire & Document Set
              </h3>
              <button onClick={handleClose} className="p-1 hover:bg-gray-100 rounded-full transition-colors" disabled={isRenaming}>
                <X size={20} className="text-gray-500" />
              </button>
            </div>
            <div className="w-full flex flex-col items-start gap-1.5">
              <label htmlFor="questionnaireName" className="text-sm font-normal text-[#18181B]">
                Name of Questionnaire & Document Set
              </label>
              <Input
                id="questionnaireName"
                placeholder="Enter name of questionnaire"
                value={questionnaireName}
                onChange={(e) => {
                  setQuestionnaireName(e.target.value);
                }}
                maxLength={45}
                className="w-full"
                classNames={{
                  inputWrapper: `bg-[#FAFAFA] border rounded-lg h-auto px-4 py-2 ${errorMessage ? 'border-[#F31260]' : 'border-[#E4E4E7]'}`,
                  input: "text-sm placeholder:text-[#A1A1AA]",
                }}
                variant="flat"
                isInvalid={!!errorMessage}
                isDisabled={isRenaming}
              />
              {errorMessage && (
                <div className="text-[#F31260] text-xs mt-1 px-1">
                  {errorMessage}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-1 pt-1 mt-auto">
              <div className="w-full border-t border-[#DCDCDC]" />
              <div className="flex justify-end gap-2 w-full pt-2">
                <ZButton
                  variant="bordered"
                  onClick={handleClose}
                  className="h-7 px-3 text-sm rounded-md border-[#006FEE] text-[#006FEE] font-medium"
                  isDisabled={isRenaming}
                >
                  Cancel
                </ZButton>
                <ZButton
                  color="primary"
                  onClick={handleSubmit}
                  isDisabled={isSubmitDisabled}
                  isLoading={isRenaming}
                  className="h-7 px-3 text-sm rounded-md bg-[#006FEE] text-white font-medium"
                >
                  Submit
                </ZButton>
              </div>
            </div>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default QuestionnaireRenameModal;